import type { ReactNode } from 'react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { formatNumeric } from '@/lib/utils/stringUtils'

/**
 * Analytics Overview building blocks (White signal v2, Paper "App v3 ·
 * Analytics Overview · LP-aligned"). Raised white cards inside the page
 * `.well`, bar-in-row tables with gray fills, and a small segmented control.
 */

export interface OverviewTabOption<T extends string> {
  value: T
  label: string
}

/** Paper segmented control: gray track (2px pad), white active chip with a 1px drop. */
export function OverviewTabs<T extends string>({
  options,
  value,
  onChange,
  className,
  ariaLabel,
}: {
  options: OverviewTabOption<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
  ariaLabel?: string
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'flex shrink-0 overflow-x-auto scrollbar-hide rounded-md bg-foreground/[0.05] p-0.5',
        className,
      )}
    >
      {options.map((opt) => {
        const active = opt.value === value
        return (
          <button
            key={opt.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              'flex h-[22px] shrink-0 items-center whitespace-nowrap rounded-[5px] px-2 text-xs leading-4 transition-colors',
              active
                ? 'bg-card font-medium text-foreground shadow-[0_1px_2px_rgb(0_0_0/0.08)]'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}

/** Card title: 14.5px / 600, the one weight bump the design allows on a card. */
export function OverviewCardTitle({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={cn('text-sm font-semibold leading-[18px] text-foreground', className)}>
      {children}
    </h3>
  )
}

/** Centered footer link row: "View all pages", "Open all visitors". */
export function OverviewCardFooter({
  label,
  onClick,
  className,
}: {
  label: string
  onClick?: () => void
  className?: string
}) {
  return (
    <div className={cn('flex justify-center border-t border-border/60 pt-2.5 pb-3', className)}>
      <button
        type="button"
        onClick={onClick}
        className="text-xs font-medium leading-4 text-muted-foreground transition-colors hover:text-foreground"
      >
        {label}
      </button>
    </div>
  )
}

export interface OverviewBreakdownRow {
  /** Display label */
  label: string
  value: number
  /** Value passed to onRowClick (defaults to label) */
  key?: string
}

interface OverviewBreakdownCardProps<T extends string> {
  title: string
  tabs: OverviewTabOption<T>[]
  tab: T
  onTabChange: (tab: T) => void
  /** Column header over the labels (e.g. "Page", "Channel") */
  dimensionLabel: string
  /** Column header over the numbers (e.g. "Visitors", "Entries") */
  valueLabel: string
  rows: OverviewBreakdownRow[]
  /**
   * Denominator for the Share column and the bar width. Pass the period's
   * deduped unique-visitor total, never a sum of rows (visitors are not additive).
   */
  total: number
  /** Rows shown; the design runs 5 on the first pair, 4 on the second */
  limit?: number
  showColumnHeader?: boolean
  isLoading?: boolean
  onRowClick?: (key: string) => void
  footerLabel?: string
  onFooterClick?: () => void
  className?: string
  /** Extra control at the far right of the header, e.g. a `ChartViewToggle` */
  headerAction?: ReactNode
  /** Replaces the column header and rows (an alternate chart view) */
  alternateBody?: ReactNode
}

const VALUE_COL = 'w-[70px] shrink-0 text-right'
const SHARE_COL = 'w-14 shrink-0 text-right'

export function OverviewBreakdownCard<T extends string>({
  title,
  tabs,
  tab,
  onTabChange,
  dimensionLabel,
  valueLabel,
  rows,
  total,
  limit = 5,
  showColumnHeader = true,
  isLoading = false,
  onRowClick,
  footerLabel,
  onFooterClick,
  className,
  headerAction,
  alternateBody,
}: OverviewBreakdownCardProps<T>) {
  const shown = rows.slice(0, limit)

  return (
    <Card className={cn('gap-0 py-0', className)}>
      <div className="flex items-center gap-2.5 px-[18px] py-3.5">
        <OverviewCardTitle className="min-w-0 flex-1 truncate">{title}</OverviewCardTitle>
        <OverviewTabs options={tabs} value={tab} onChange={onTabChange} ariaLabel={`${title} breakdown`} />
        {headerAction}
      </div>

      {alternateBody && !isLoading ? (
        <div className="flex flex-1 flex-col">{alternateBody}</div>
      ) : (
      <>
      {showColumnHeader && (
        <div className="flex px-[18px] pb-1.5 text-xs leading-4 text-muted-foreground">
          <span className="min-w-0 flex-1">{dimensionLabel}</span>
          <span className={VALUE_COL}>{valueLabel}</span>
          <span className={SHARE_COL} title="Share of unique visitors in the period">
            Share
          </span>
        </div>
      )}

      <div className="flex flex-1 flex-col pb-1.5">
        {isLoading ? (
          Array.from({ length: limit }).map((_, i) => (
            <div key={i} className="relative mx-2.5 h-9 shrink-0">
              <div
                className="absolute left-0 top-[3px] h-[30px] animate-pulse rounded-md bg-muted"
                style={{ width: `${[62, 40, 28, 18, 12][i % 5]}%` }}
              />
            </div>
          ))
        ) : shown.length === 0 ? (
          <div className="flex flex-1 items-center justify-center px-[18px] py-8 text-center text-[13px] text-muted-foreground">
            No data for this range yet.
          </div>
        ) : (
          shown.map((row) => {
            const share = total > 0 ? Math.min(100, (row.value / total) * 100) : 0
            const shareLabel = total > 0 ? `${Math.round(share)}%` : '—'
            const clickable = Boolean(onRowClick)
            return (
              <button
                key={row.key ?? row.label}
                type="button"
                disabled={!clickable}
                onClick={() => onRowClick?.(row.key ?? row.label)}
                title={row.label}
                className={cn(
                  'group relative mx-2.5 flex h-9 shrink-0 items-center px-2 text-left',
                  clickable ? 'cursor-pointer' : 'cursor-default',
                )}
              >
                <span
                  aria-hidden
                  className="absolute left-0 top-[3px] h-[30px] rounded-md bg-primary-tint transition-colors group-hover:bg-primary-tint-ring"
                  style={{ width: `${Math.max(share, 1)}%` }}
                />
                <span className="relative min-w-0 flex-1 truncate text-[13px] leading-4 text-foreground">
                  {row.label}
                </span>
                <span className={cn(VALUE_COL, 'relative text-[13px] font-medium leading-4 text-foreground tabular-nums')}>
                  {formatNumeric(row.value)}
                </span>
                <span className={cn(SHARE_COL, 'relative text-xs leading-4 text-muted-foreground tabular-nums')}>
                  {shareLabel}
                </span>
              </button>
            )
          })
        )}
      </div>
      </>
      )}

      {footerLabel && <OverviewCardFooter label={footerLabel} onClick={onFooterClick} className="mt-auto" />}
    </Card>
  )
}

/**
 * The period's deduped unique-visitor total (the number the Overview strip
 * shows). Breakdown shares divide by this, never by a sum of rows.
 */
export function getPeriodUniqueVisitors(metrics: any): number {
  const n = Number(metrics?.trends?.totalSessions?.[0]?.total_sessions ?? 0)
  return Number.isFinite(n) ? n : 0
}
